<template>
  <div class="container">
    <div id="coursesContainer">
      <div v-if="loading" class="loading">加载中...</div>
      <div v-else-if="currentCourses.length === 0" class="no-courses">
        该日期暂无课程安排
      </div>
      <div v-else>
        <div class="course-grid">
          <div
            v-for="course in currentCourses"
            :key="course.id"
            class="course-card"
          >
            <!-- 视频课程卡片 -->
            <div
              class="video-card"
              @mouseenter="onVideoHover(course, true)"
              @mouseleave="onVideoHover(course, false)"
              @click="goToVideoDetail(course)"
            >
              <!-- 视频容器 -->
              <div class="video-container">
                <video
                  :ref="'videoPlayer' + course.id"
                  class="course-video"
                  preload="metadata"
                  muted
                  playsinline
                >
                  <source :src="getVideoUrl(course.videoUrl)" type="video/mp4" />
                  您的浏览器不支持HTML5视频播放
                </video>

                <!-- 播放按钮覆盖层 -->
                <div
                  class="video-overlay"
                  :class="{ hidden: isVideoPlaying(course) }"
                >
                  <div class="play-icon">▶</div>
                </div>
              </div>

              <!-- 视频信息 -->
              <div class="video-info">
                <div class="video-title">{{ course.title }}</div>
                <div class="video-meta">
                  <span class="teacher">讲师: {{ course.teachername }}</span>
                </div>
              </div>
            </div>

            <!-- 课程描述 -->
            <div class="course-desc" v-html="course.content"></div>
          </div>
        </div>

        <!-- 分页组件 -->
        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            :page-size="pageSize"
            :current-page="pageNum"
            :page-sizes="[5, 10, 20, 50]"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOnlineClassList } from "@/api/processApi";
import { baseURL } from "@/api/axiosConfig";

export default {
  data() {
    return {
      currentCourses: [],
      loading: false,
      playingVideos: {}, // 存储正在播放的视频ID
      // 分页参数
      pageNum: 1,
      pageSize: 8,
      total: 0,
      pages: 1,
    };
  },
  created() {
    this.fetchCourses();
  },
  beforeDestroy() {
    // 清理所有视频播放器
    Object.values(this.$refs).forEach((player) => {
      if (player && player.pause) player.pause();
    });
  },
  methods: {
    fetchCourses(pageNum = this.pageNum, pageSize = this.pageSize) {
      this.loading = true;
      this.pageNum = pageNum;
      this.pageSize = pageSize;

      getOnlineClassList({ pageNum, pageSize }).then((response) => {
        if (response.data.code === 200) {
          const data = response.data.data;
          this.currentCourses = data.list || [];
          this.total = data.total || 0;
          this.pages = data.pages || 1;
        } else {
          this.currentCourses = [];
          this.total = 0;
          this.pages = 1;
        }
      }).catch((error) => {
        console.error("获取在线课堂列表失败:", error);
        this.currentCourses = [];
        this.total = 0;
        this.pages = 1;
      }).finally(() => {
        this.loading = false;
      });
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchCourses(1, val);
    },
    handleCurrentChange(val) {
      this.fetchCourses(val, this.pageSize);
    },
    // 视频悬停处理
    onVideoHover(course, isHover) {
      const videoRef = `videoPlayer${course.id}`;
      const videoElement = this.$refs[videoRef]?.[0];

      if (!videoElement) return;

      if (isHover) {
        // 鼠标进入时播放视频
        this.playingVideos[course.id] = true;
        videoElement.play().catch((e) => {
          console.error("视频播放失败:", e);
          this.playingVideos[course.id] = false;
        });
      } else {
        // 鼠标离开时暂停视频并重置
        this.playingVideos[course.id] = false;
        videoElement.pause();
        videoElement.currentTime = 0;
      }
    },
    // 检查视频是否正在播放
    isVideoPlaying(course) {
      return this.playingVideos[course.id];
    },
    // 跳转到视频详情页
    goToVideoDetail(course) {
      // 在实际应用中，这里应该跳转到视频详情页
      this.$router.push({
        name: "CourseVideoDetail",
        params: { id: course.id },
      });

      // 演示：在当前页面模拟详情页效果
      // alert(
      //   `将跳转到视频详情页: ${course.name}\n实际应用中这里会打开视频播放页`
      // );
    },
    getVideoUrl(videoUrl) {
      if (!videoUrl) return "";
      // 去除前后的反引号、空格和可能的引号
      let url = videoUrl.trim().replace(/^[`"'\\s]+|[`"'\\s]+$/g, '');
      
      // 如果已经是完整的HTTP URL，直接返回
      if (url.startsWith("http")) {
        console.log("获取到的视频URL:", url);
        return url;
      }
      
      // 否则拼接视频服务器地址
      return baseURL + url;
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 1440px;
  width: calc(100% - 20px);
  margin: 60px auto;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  padding: 20px;
  box-sizing: border-box;
}

h1 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
}

/* 新增课程网格布局 */
.course-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
}

.course-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
}

.course-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

/* 视频卡片样式 */
.video-card {
  cursor: pointer;
  position: relative;
}

.video-container {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 宽高比 */
  background-color: #000;
  overflow: hidden;
}

.course-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s;
}

.video-overlay.hidden {
  opacity: 0;
}

.play-icon {
  width: 60px;
  height: 60px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  color: #ff4d4f;
  transition: transform 0.2s;
}

.video-card:hover .play-icon {
  transform: scale(1.1);
}

.video-info {
  padding: 15px;
}

.video-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 3em;
  line-height: 1.5em;
}

.video-meta {
  display: flex;
  justify-content: space-between;
  color: #666;
  font-size: 13px;
}

.teacher {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70%;
}

.duration {
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  border-radius: 2px;
  position: absolute;
  right: 10px;
  bottom: 10px;
}

.course-desc {
  padding: 0 15px 15px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 3em;
}

.no-courses {
  text-align: center;
  padding: 40px;
  color: #777;
  font-size: 18px;
  grid-column: 1 / -1;
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 20px 0;
  margin-top: 20px;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 18px;
  grid-column: 1 / -1;
}
</style>
